import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  HelpCircle
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import HelpModal from '../../components/HelpModal/HelpModal';
import { burnExpenseService } from '../../services/burnExpenseService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'react-toastify';

const getCurrentMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(v || 0);

const BudgetVsActual = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadData();
  }, [month]);

  const loadData = async () => {
    setLoading(true);
    try {
      const r = await burnExpenseService.getBudgetVsActual({ month });
      if (r?.success) setData(r.data);
      else setData(null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load budget vs actual');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Reports', path: '/expenses?tab=reports', onClick: () => navigate('/expenses?tab=reports') },
    { label: 'Budget vs Actual', path: '/expenses/budget-vs-actual', isCurrent: true }
  ];

  const helpContent = (
    <>
      <h3>What is Budget vs Actual?</h3>
      <p>This report shows <span className="help-term">Indian FY burn budget compared to approved actuals by month</span>. It uses the approved Financial Year budget and approved burn expense entries for the selected month to show totals and category-wise variance.</p>

      <h3>Navigation</h3>
      <p>Use the breadcrumb <span className="help-term">Expenses</span> or <span className="help-term">Reports</span> to go back. No separate back arrow is shown; navigation is through the breadcrumb only.</p>

      <h3>Month selector</h3>
      <p>Use the <strong>Month</strong> dropdown (e.g. 2026-02) to choose which month to report on. Data is based on the financial year that contains that month (Indian FY April–March).</p>

      <h3>Summary cards</h3>
      <ul>
        <li><strong>Total Budget</strong> – Sum of approved FY budget for the selected month (and FY).</li>
        <li><strong>Total Actual</strong> – Sum of approved burn expense entries for that month.</li>
        <li><strong>Variance</strong> – Difference (actual − budget); positive means over budget, with percentage.</li>
      </ul>

      <h3>By Category table</h3>
      <p>Each row is a burn expense category with <strong>Budget</strong>, <strong>Actual</strong>, <strong>Variance</strong>, <strong>%</strong>, and <strong>Status</strong> (e.g. OVER or under). If you see &quot;No data for this month&quot;, create and approve an FY budget and have approved burn entries for that month.</p>

      <h3>Chart</h3>
      <p>When data exists, a bar chart compares Budget and Actual by category for quick visual comparison.</p>
    </>
  );

  const byCategory = data?.byCategory || [];
  const totalBudget = data?.totalBudget ?? 0;
  const totalActual = data?.totalActual ?? 0;
  const totalVariance = data?.totalVariance ?? 0;
  const totalVariancePercent = data?.totalVariancePercent ?? 0;

  const chartData = byCategory.map((c) => ({
    name: c.categoryName,
    budget: c.budgetAmount,
    actual: c.actualAmount
  }));

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>Budget vs Actual</h1>
          <p>Indian FY burn budget vs approved actuals by month</p>
        </div>
        <div className="page-actions">
          <label className="form-label mb-0 me-2">Month</label>
          <input
            type="month"
            className="form-control"
            style={{ width: 'auto', minWidth: 160 }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</p>
        </div>
      )}

      {!loading && data && (
        <>
          {data.message && (
            <div className="alert alert-info">{data.message}</div>
          )}

          {/* Summary cards */}
          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
              </div>
              <div className="metric-value">{formatINR(totalBudget)}</div>
              <div className="metric-title">Total Budget</div>
              <div className="metric-subtitle">{data.financialYear || ''} • {month}</div>
            </div>
            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
              </div>
              <div className="metric-value">{formatINR(totalActual)}</div>
              <div className="metric-title">Total Actual</div>
              <div className="metric-subtitle">Approved burn entries</div>
            </div>
            <div className={`metric-card ${totalVariance > 0 ? 'churn' : 'burn'}`}>
              <div className="metric-header">
                <div className="metric-icon">
                  {totalVariance > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                </div>
              </div>
              <div className="metric-value" style={{ color: totalVariance > 0 ? '#dc3545' : '#198754' }}>
                {totalVariance > 0 ? '+' : ''}{formatINR(totalVariance)}
              </div>
              <div className="metric-title">Variance</div>
              <div className="metric-subtitle">
                {totalVariancePercent !== 0 ? (totalVariancePercent > 0 ? '+' : '') + totalVariancePercent.toFixed(1) + '%' : '—'}
              </div>
            </div>
          </div>

          {/* Table */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart3 size={20} />
              By Category
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      Category
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      Budget
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      Actual
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      Variance
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      %
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {byCategory.length > 0 ? (
                    byCategory.map((c, i) => {
                      const isOver = c.status === 'OVER';
                      const rowBg = isOver ? 'rgba(220, 53, 69, 0.08)' : 'rgba(25, 135, 84, 0.06)';
                      const rowColor = isOver ? '#721c24' : '#155724';
                      return (
                        <tr
                          key={c.categoryId || i}
                          style={{
                            borderBottom: '1px solid #f1f3f4',
                            backgroundColor: rowBg
                          }}
                        >
                          <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: rowColor }}>
                            {c.categoryName}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right', color: '#495057' }}>
                            {formatINR(c.budgetAmount)}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>
                            {formatINR(c.actualAmount)}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: '600',
                              color: isOver ? '#dc3545' : '#198754'
                            }}
                          >
                            {c.variance > 0 ? '+' : ''}{formatINR(c.variance)}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: '600',
                              color: isOver ? '#dc3545' : '#198754'
                            }}
                          >
                            {c.variancePercent != null
                              ? (c.variancePercent > 0 ? '+' : '') + c.variancePercent.toFixed(1) + '%'
                              : '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span
                              style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                background: isOver ? '#f8d7da' : '#d4edda',
                                color: isOver ? '#721c24' : '#155724'
                              }}
                            >
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                        No data for this month. Create and approve an FY budget first.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bar chart */}
          {chartData.length > 0 && (
            <div
              style={{
                background: '#fff',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '24px',
                marginBottom: '24px'
              }}
            >
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                Budget vs Actual
              </h3>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `₹${v / 1000}k`} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(v) => [formatINR(v), '']}
                      labelFormatter={(l) => l}
                      contentStyle={{ fontSize: '13px' }}
                    />
                    <Legend />
                    <Bar dataKey="budget" name="Budget" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" name="Actual" fill="#198754" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !data && (
        <div className="alert alert-info">Select a month to view budget vs actual.</div>
      )}

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Budget vs Actual – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default BudgetVsActual;
